import { Phone, MessageCircle, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import LocalLink from "@/components/LocalLink";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackWhatsAppClick, trackPhoneClick } from "@/lib/gtag";
import { useMobileMenuOpen } from "@/hooks/useMobileMenuOpen";
import { stripLocalePrefix, isPaidLandingPath } from "@/lib/seo";

const StickyActionBar = () => {
  const { t } = useLanguage();
  const isMobileMenuOpen = useMobileMenuOpen();
  const { pathname } = useLocation();

  /**
   * Em landing page de trafego pago o pill "Start Your Defense" nao aparece.
   *
   * Ele aponta para /contact, que tem outro formulario. Quem clica nele sai da
   * LP e o lead passa a ser enviado sem o form_name "lp_queens_openai_ads" e
   * sem case_subcategory, ou seja, chega no GoHighLevel sem atribuicao de
   * campanha — e, pior, trackFormSubmission() nunca roda, entao o OpenAI Ads
   * nao recebe o lead_created daquele clique e otimiza a campanha com um sinal
   * a menos. Clique de anuncio pago saindo da pagina paga e dinheiro fora.
   *
   * WhatsApp e telefone ficam: nao sao saida, sao conversao, e nesse publico
   * no celular costumam ser o caminho principal.
   */
  const onPaidLanding = isPaidLandingPath(stripLocalePrefix(pathname));

  return (
    <>
      {/*
        Os dois links do WhatsApp levam mensagem pre-preenchida desde
        20/10/2026. O texto aqui e generico de proposito: este componente
        aparece no site inteiro e nao sabe em que pagina esta. As LPs de
        trafego pago trazem no proprio hero uma versao especifica, com o nome
        da campanha, que e o que permite a triagem saber de onde veio a
        conversa — o wa.me nao carrega metadado nenhum.
      */}
      {/* ─── MOBILE: Full-width bottom bar ─── */}
      <motion.div
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 1 }}
        className={`fixed bottom-0 left-0 right-0 z-[9999] md:hidden border-t border-white/10 ${isMobileMenuOpen ? "hidden" : "flex"}`}
      >
        <a
          href={`https://wa.me/12125860500?text=${encodeURIComponent(t("sticky.waText"))}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackWhatsAppClick}
          className="flex-1 flex items-center justify-center gap-2 h-[60px] bg-[#25D366] font-semibold text-sm text-white transition-all active:scale-95"
        >
          <MessageCircle className="w-5 h-5" />
          <span>{t("sticky.whatsapp")}</span>
        </a>
        <a
          href="tel:+12125860500"
          onClick={trackPhoneClick}
          className="flex-1 flex items-center justify-center gap-2 h-[60px] font-semibold text-sm transition-all active:scale-95"
          style={{ background: "var(--gold-gradient)" }}
        >
          <Phone className="w-5 h-5 text-primary" />
          <span className="text-primary">{t("sticky.call")}</span>
        </a>
      </motion.div>

      {/* ─── DESKTOP: Floating pills bottom-right ─── */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 1 }}
        className="fixed bottom-5 right-5 z-[9999] hidden md:flex flex-col items-end gap-3"
      >
        {/* WhatsApp pill */}
        <a
          href={`https://wa.me/12125860500?text=${encodeURIComponent(t("sticky.waText"))}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackWhatsAppClick}
          className="group flex items-center gap-2.5 px-5 py-3 rounded-full bg-[#25D366] text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
        >
          <span className="relative">
            <MessageCircle className="w-5 h-5" />
            {/* Online dot */}
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-300 rounded-full border-2 border-[#25D366] animate-pulse" />
          </span>
          <span>{t("sticky.whatsappDesktop")}</span>
        </a>

        {/* Start Your Defense pill — fora das LPs pagas, ver comentario acima */}
        {!onPaidLanding && (
          <LocalLink
            to="/contact"
            className="group flex items-center gap-2.5 px-6 py-3.5 rounded-full font-bold text-sm text-primary shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            style={{ background: "var(--gold-gradient)" }}
          >
            <Shield className="w-5 h-5" />
            <span>{t("sticky.startDefense")}</span>
          </LocalLink>
        )}
      </motion.div>
    </>
  );
};

export default StickyActionBar;
